import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type ContextValue = {
  register: (key: string, dirty: boolean) => void;
  confirmDiscard: () => boolean;
};

const LoanSectionFormContext = createContext<ContextValue | null>(null);

export function LoanSectionFormProvider({ children }: { children: ReactNode }) {
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(() => new Set());

  const register = useCallback((key: string, dirty: boolean) => {
    setDirtyKeys((prev) => {
      const has = prev.has(key);
      if (dirty && has) return prev;
      if (!dirty && !has) return prev;
      const next = new Set(prev);
      if (dirty) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const confirmDiscard = useCallback(() => {
    if (dirtyKeys.size === 0) return true;
    return window.confirm(
      'You have unsaved changes in this section. Discard them and continue?'
    );
  }, [dirtyKeys]);

  const value = useMemo(
    () => ({ register, confirmDiscard }),
    [register, confirmDiscard]
  );

  return (
    <LoanSectionFormContext.Provider value={value}>
      {children}
    </LoanSectionFormContext.Provider>
  );
}

export function useLoanSectionDirty(key: string, isDirty: boolean) {
  const ctx = useContext(LoanSectionFormContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.register(key, isDirty);
    return () => ctx.register(key, false);
  }, [ctx, key, isDirty]);
}

export function useLoanSectionFormNav() {
  const ctx = useContext(LoanSectionFormContext);
  return ctx?.confirmDiscard ?? (() => true);
}
